import { useState } from 'react'
import { api, errorText } from '../api/client'
import type { Subject } from '../api/types'
import { useAsync } from '../lib/useAsync'
import { Icon } from './Icon'
import { useToast } from './Toast'

/** Removable tag pills plus an inline "＋ Tag" input with suggestions from tags
 *  already in use, so a team converges on one spelling. */
export function TagEditor({ subject, tags, onChange }: {
  subject: Subject
  tags: string[]
  onChange: (tags: string[]) => void
}) {
  const [adding, setAdding] = useState(false)
  const [value, setValue] = useState('')
  const toast = useToast()
  const { data: known } = useAsync(() => (adding ? api.tags() : Promise.resolve(null)), [adding])

  async function add() {
    const tag = value.trim()
    if (!tag) {
      setAdding(false)
      return
    }
    try {
      onChange((await api.addTag(subject, tag)).tags)
      setValue('')
      setAdding(false)
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    }
  }

  async function remove(tag: string) {
    try {
      onChange((await api.removeTag(subject, tag)).tags)
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    }
  }

  return (
    <div className="tagrow">
      {tags.map((t) => (
        <span className="tag" key={t}>
          {t}
          <button onClick={() => remove(t)} aria-label={`Remove tag ${t}`}>
            <Icon name="close" size={13} />
          </button>
        </span>
      ))}
      {adding ? (
        <>
          <input
            className="tag-input"
            autoFocus
            value={value}
            list="known-tags"
            maxLength={40}
            placeholder="Tag name"
            onChange={(e) => setValue(e.target.value)}
            onBlur={add}
            onKeyDown={(e) => {
              if (e.key === 'Enter') add()
              if (e.key === 'Escape') {
                setValue('')
                setAdding(false)
              }
            }}
          />
          <datalist id="known-tags">
            {(known?.tags ?? []).filter((k) => !tags.includes(k.tag)).map((k) => (
              <option key={k.tag} value={k.tag} />
            ))}
          </datalist>
        </>
      ) : (
        <button className="tag-add" onClick={() => setAdding(true)}>＋ Tag</button>
      )}
    </div>
  )
}
